import type { TreeSelectItemBase } from '@/components/Select/types';
import { MainText } from '@/components/Typography';
import useProductStore from '@/features/Product/store/ProductStore';
import type { ProductT } from '@/features/Product/types';
import { filterMap } from '@/utils/lazy';

export const useFetchProductTree = () => {
  const productMap = useProductStore((state) => state.productMap);
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  return async (searchValue?: string): Promise<TreeSelectItemBase[]> => {
    return createProductField({
      productMap,
      brandMap,
      lineMap,
      searchValue,
    });
  };
};

export const useFetchOptionTree = () => {
  const productMap = useProductStore((state) => state.productMap);
  return async (searchValue?: string) => {
    return createOptionField(productMap, searchValue);
  };
};

const productPropertyFetcher = {
  useFetchProductTree,
  useFetchOptionTree,
};

export default productPropertyFetcher;

export const createProductField = ({
  productMap,
  brandMap,
  lineMap,
  searchValue,
}: {
  productMap: Map<string, ProductT>;
  brandMap: Map<string, ProductBrandOption>;
  lineMap: Map<string, ProductLineOption>;
  searchValue?: string;
}): TreeSelectItemBase[] => {
  // 검색값이 있는 경우 필터링
  let filteredProducts = Array.from(productMap.values());
  if (searchValue) {
    const lowerSearchValue = searchValue.toLowerCase().replace(/\s/g, '');
    filteredProducts = filteredProducts.filter((product) => {
      return `${product.product_name}_${
        brandMap.get(product.brand_id || '')?.name
      }_${lineMap.get(product.line_id || '')?.name}`
        ?.toLowerCase()
        .replace(/\s/g, '')
        .includes(lowerSearchValue);
    });

    return filteredProducts.map((product) => {
      const brandName = brandMap.get(product.brand_id || '')?.name;
      const lineName = lineMap.get(product.line_id || '')?.name;
      return {
        alias: product.product_id,
        searchValue: product.product_name || '',
        node: (
          <MainText>
            <MainText dim>
              {brandName && `${brandName} / `} {lineName && `${lineName} / `}
            </MainText>
            <MainText>{product.product_name || ''}</MainText>
          </MainText>
        ),
      };
    });
  }

  // 브랜드가 있는 제품과 없는 제품 분리
  const productsWithBrand = filteredProducts.filter(
    (product) => product.brand_id,
  );
  const productsWithoutBrand = filteredProducts.filter(
    (product) => !product.brand_id,
  );

  // 모든 제품이 브랜드가 없는 경우 브랜드로 묶지 않고 플랫 리스트 반환
  if (productsWithBrand.length === 0 && productsWithoutBrand.length > 0) {
    return productsWithoutBrand.map((product) => ({
      alias: product.product_id,
      searchValue: product.product_name || '',
      node: product.product_name || '',
    }));
  }

  const result: TreeSelectItemBase[] = [];

  // 브랜드별로 제품 그룹화
  const productsByBrand = new Map<string, ProductT[]>();

  // 브랜드가 있는 제품들을 브랜드별로 그룹화
  productsWithBrand.forEach((product) => {
    const brandId = product.brand_id!;
    if (!productsByBrand.has(brandId)) {
      productsByBrand.set(brandId, []);
    }
    productsByBrand.get(brandId)!.push(product);
  });

  // 브랜드가 없는 제품들을 'others' 그룹에 추가
  if (productsWithoutBrand.length > 0) {
    productsByBrand.set('others', productsWithoutBrand);
  }

  // 브랜드별로 StructuredFilterItem 생성
  productsByBrand.forEach((products, brandId) => {
    let brandName: string;

    if (brandId === 'others') {
      brandName = 'Others';
    } else {
      const brand = brandMap.get(brandId);
      brandName = brand?.name || brandId;
    }

    // 브랜드 아이템 생성
    const brandItem: TreeSelectItemBase = {
      alias: brandId,
      searchValue: brandName,
      node: brandName,
      checkOption: {
        disable: true,
        exceptSelf: true,
      },

      childNodes: products.map((product) => ({
        alias: product.product_id,
        searchValue: product.product_name || '',
        node: (
          <span>
            {product.line_id && (
              <span className="text-gray-600">
                {lineMap.get(product.line_id)?.name} /{' '}
              </span>
            )}
            {product.product_name}
          </span>
        ),
      })),
    };

    result.push(brandItem);
  });

  return result;
};

export const createOptionField = (
  productMap: Map<string, ProductT>,
  searchValue?: string,
): TreeSelectItemBase[] => {
  // searchValue가 있는 경우 검색 결과를 반환
  const result: TreeSelectItemBase[] = [];
  productMap.values().forEach((product) => {
    if (searchValue) {
      product.options?.forEach((option) => {
        if (
          option.option_name.toLowerCase().includes(searchValue.toLowerCase())
        ) {
          result.push({
            alias: option.id,
            searchValue: option.option_name,
            node: (
              <MainText>
                <MainText dim>{product.product_name} / </MainText>
                {option.option_name}
              </MainText>
            ),
          });
        }
      });
    } else {
      result.push({
        alias: product.product_id,
        searchValue: product.product_name || '',
        node: product.product_name || '',
        checkOption: {
          disable: true,
          exceptSelf: true,
        },
        childNodes: filterMap(product.options || [], (option) => {
          return {
            alias: option.id,
            searchValue: option.option_name,
            node: option.option_name,
          };
        }),
      });
    }
  });

  return result;
};
